// src/scenes/contributionPolicy/index.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  useTheme,
  Button,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Typography,
  Divider,
  TextField,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Header from "components/Header";
import { gql, useMutation, useQuery } from "@apollo/client";
import { DataGrid } from "@mui/x-data-grid";

import { groupClient } from "state/apolloClient";
import MonthlyContributionForm from "./monthlyContributionForm";

// =============================
// GraphQL
// =============================
const GET_MONTHLY_CONTRIBUTIONS = gql`
  query {
    monthlyContributions {
      id
      amount
      dueDate
      groupId
      isActive
      createdBy
      createdDate
      updatedBy
      updatedDate
    }
  }
`;

const CREATE_MONTHLY_CONTRIBUTION = gql`
  mutation CreateMonthlyContribution($newMonthlyContribution: MonthlyContributionsInput!) {
    createMonthlyContribution(newMonthlyContribution: $newMonthlyContribution) {
      id
      amount
      dueDate
      groupId
      isActive
      createdBy
      createdDate
      updatedBy
      updatedDate
    }
  }
`;

const ACTIVATE_DEACTIVATE_MONTHLY = gql`
  mutation ActivateDeactivateMonthlyContribution($record: MonthlyContributionsInput!) {
    activateDeactivateMonthlyContribution(record: $record) {
      status
      errorMessage
      data
    }
  }
`;

const POPULATE_CONTRIBUTION_SCHEDULE = gql`
  mutation PopulateContributionSchedule($input: sp_PopulateContributionScheduleInput!) {
    populateContributionSchedule(input: $input) {
      status
      errorMessage
    }
  }
`;

const REQUEST_SCHEDULE_DELETION = gql`
  mutation RequestContributionScheduleDeletion($req: ContributionScheduleDeletionRequestInput!) {
    requestContributionScheduleDeletion(req: $req) {
      status
      errorMessage
      data
    }
  }
`;

// NOTE: This looks mismatched to your SDL earlier. Keep if your BE truly expects it.
const APPROVE_SCHEDULE_DELETION = gql`
  mutation ApproveContributionScheduleDeletion($deletionRequest: ContributionScheduleDeletionApprovalInput!) {
    approveContributionScheduleDeletion(deletionRequest: $deletionRequest) {
      status
      errorMessage
    }
  }
`;

// =============================
// Styling
// =============================
const gridSx = (theme) => ({
  "& .MuiDataGrid-root": { border: "none" },
  "& .MuiDataGrid-cell": { borderBottom: "none" },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: theme.palette.background.alt,
    color: theme.palette.secondary[100],
    borderBottom: "none",
  },
  "& .MuiDataGrid-virtualScroller": {
    backgroundColor: theme.palette.primary.light,
  },
  "& .MuiDataGrid-footerContainer": {
    backgroundColor: theme.palette.background.alt,
    color: theme.palette.secondary[100],
    borderTop: "none",
    minHeight: 44,
  },
});

// =============================
// Error helper (Apollo/GraphQL)
// =============================
const getApolloErrorMessage = (err) => {
  const gqlErr = err?.graphQLErrors?.[0];
  if (gqlErr) {
    const extMsg = gqlErr?.extensions?.message;
    if (typeof extMsg === "string" && extMsg.trim()) return extMsg;
    if (typeof gqlErr.message === "string" && gqlErr.message.trim()) return gqlErr.message;
  }

  const netErrMsg =
    err?.networkError?.result?.errors?.[0]?.extensions?.message ||
    err?.networkError?.result?.errors?.[0]?.message;

  if (typeof netErrMsg === "string" && netErrMsg.trim()) return netErrMsg;
  if (typeof err?.message === "string" && err.message.trim()) return err.message;

  return "An unexpected error occurred. Please try again.";
};

const ContributionPolicy = () => {
  const theme = useTheme();

  // =============================
  // Data
  // =============================
  const { data: mcData, loading: mcLoading, refetch: refetchMonthly } = useQuery(
    GET_MONTHLY_CONTRIBUTIONS,
    {
      client: groupClient,
      fetchPolicy: "cache-and-network",
    }
  );

  const monthlyContributions = useMemo(() => mcData?.monthlyContributions || [], [mcData]);

  // Active first, then newest
  const sortedMonthly = useMemo(() => {
    const rows = [...monthlyContributions];
    rows.sort((a, b) => {
      const aActive = a?.isActive ? 1 : 0;
      const bActive = b?.isActive ? 1 : 0;
      if (aActive !== bActive) return bActive - aActive;

      const ad = a?.createdDate ? new Date(a.createdDate).getTime() : 0;
      const bd = b?.createdDate ? new Date(b.createdDate).getTime() : 0;
      return bd - ad;
    });
    return rows;
  }, [monthlyContributions]);

  const activeMonthly = useMemo(
    () => sortedMonthly.find((x) => x?.isActive) || null,
    [sortedMonthly]
  );

  // =============================
  // Mutations
  // =============================
  const [createMonthlyContribution, { loading: creatingMonthly }] = useMutation(
    CREATE_MONTHLY_CONTRIBUTION,
    { client: groupClient }
  );

  const [activateDeactivateMonthly, { loading: togglingMonthly }] = useMutation(
    ACTIVATE_DEACTIVATE_MONTHLY,
    { client: groupClient }
  );

  const [populateSchedule, { loading: populating }] = useMutation(
    POPULATE_CONTRIBUTION_SCHEDULE,
    { client: groupClient }
  );

  const [requestDeletion, { loading: requestingDeletion }] = useMutation(
    REQUEST_SCHEDULE_DELETION,
    { client: groupClient }
  );

  const [approveDeletion, { loading: approvingDeletion }] = useMutation(
    APPROVE_SCHEDULE_DELETION,
    { client: groupClient }
  );

  // =============================
  // Local state
  // =============================
  const [toast, setToast] = useState({ type: "success", msg: "" });
  const showToast = (type, msg) => setToast({ type, msg });

  // Populate schedule form
  const [popYear, setPopYear] = useState(String(new Date().getFullYear()));
  const [popStartMonth, setPopStartMonth] = useState("1");
  const [popEndMonth, setPopEndMonth] = useState("12");

  // Deletion request form
  const [delYear, setDelYear] = useState(String(new Date().getFullYear()));
  const [delStartMonth, setDelStartMonth] = useState("1");
  const [delEndMonth, setDelEndMonth] = useState("12");

  // Approve deletion form
  const [approveRequestId, setApproveRequestId] = useState("");

  // =============================
  // Handlers
  // =============================
  const handleCreateMonthly = async ({ amount, dueDate }) => {
    try {
      const amountNum = Number(amount);
      const dueNum = Number(dueDate);

      if (!amountNum || amountNum <= 0) {
        showToast("error", "Please enter a valid monthly amount.");
        return { ok: false };
      }
      if (!dueNum || dueNum < 1 || dueNum > 31) {
        showToast("error", "Due date must be between 1 and 31.");
        return { ok: false };
      }

      const variables = {
        newMonthlyContribution: {
          amount: amountNum,
          dueDate: dueNum,
        },
      };

      const { data } = await createMonthlyContribution({ variables });

      if (data?.createMonthlyContribution?.id) {
        await refetchMonthly();
        showToast("success", "Monthly contribution saved successfully.");
        return { ok: true };
      }

      showToast("error", "Failed to save monthly contribution.");
      return { ok: false };
    } catch (e) {
      console.error(e);
      showToast("error", getApolloErrorMessage(e));
      return { ok: false };
    }
  };

  const handleToggleMonthlyActive = async (row, makeActive) => {
    try {
      const { data } = await activateDeactivateMonthly({
        variables: { record: { id: Number(row.id), isActive: !!makeActive } },
      });

      const res = data?.activateDeactivateMonthlyContribution;
      if (!res) {
        showToast("error", "No response from server.");
        return;
      }

      const ok = res.status === true || String(res.status).toLowerCase() === "success";
      if (ok) {
        await refetchMonthly();
        showToast(
          "success",
          makeActive ? "Monthly contribution approved & activated." : "Monthly contribution deactivated."
        );
      } else {
        showToast("error", res.errorMessage || "Action failed.");
      }
    } catch (e) {
      console.error(e);
      showToast("error", getApolloErrorMessage(e));
    }
  };

  const handlePopulateSchedule = async () => {
    try {
      const year = String(popYear).trim();
      const startMonth = Number(popStartMonth);
      const endMonth = Number(popEndMonth);

      if (!year || year.length !== 4) return showToast("error", "Please enter a valid year e.g. 2026.");
      if (!startMonth || startMonth < 1 || startMonth > 12) return showToast("error", "Start month must be 1-12.");
      if (!endMonth || endMonth < 1 || endMonth > 12) return showToast("error", "End month must be 1-12.");
      if (startMonth > endMonth) return showToast("error", "Start month cannot be greater than end month.");

      if (!activeMonthly) {
        showToast("error", "No active monthly contribution policy found. Approve/activate one first before populating schedule.");
        return;
      }

      const { data } = await populateSchedule({
        variables: { input: { startMonth, endMonth, year } },
      });

      const result = data?.populateContributionSchedule;
      if (!result) return showToast("error", "No response from server.");

      const ok = result.status === true || String(result.status).toLowerCase() === "success";
      showToast(ok ? "success" : "error", ok ? "Contribution schedule populated successfully." : result.errorMessage || "Failed to populate schedule.");
    } catch (e) {
      console.error(e);
      showToast("error", getApolloErrorMessage(e));
    }
  };

  const handleRequestDeletion = async () => {
    try {
      const scheduleYear = String(delYear).trim();
      const startMonth = Number(delStartMonth);
      const endMonth = Number(delEndMonth);

      if (!scheduleYear || scheduleYear.length !== 4) return showToast("error", "Please enter a valid year e.g. 2026.");
      if (!startMonth || startMonth < 1 || startMonth > 12) return showToast("error", "Start month must be 1-12.");
      if (!endMonth || endMonth < 1 || endMonth > 12) return showToast("error", "End month must be 1-12.");
      if (startMonth > endMonth) return showToast("error", "Start month cannot be greater than end month.");

      const { data } = await requestDeletion({
        variables: { req: { startMonth, endMonth, scheduleYear, requestedBy: null } },
      });

      const result = data?.requestContributionScheduleDeletion;
      if (!result) return showToast("error", "No response from server.");

      const ok = result.status === true || String(result.status).toLowerCase() === "success";
      showToast(ok ? "success" : "error", ok ? "Deletion request submitted for approval." : result.errorMessage || "Failed to submit deletion request.");
    } catch (e) {
      console.error(e);
      showToast("error", getApolloErrorMessage(e));
    }
  };

  const handleApproveDeletion = async () => {
    try {
      const id = Number(approveRequestId);
      if (!id) return showToast("error", "Enter a valid deletion request ID.");

      const { data } = await approveDeletion({ variables: { deletionRequest: { id } } });

      const result = data?.approveContributionScheduleDeletion;
      if (!result) return showToast("error", "No response from server.");

      const ok = result.status === true || String(result.status).toLowerCase() === "success";
      if (ok) {
        showToast("success", "Deletion request approved.");
        setApproveRequestId("");
      } else {
        showToast("error", result.errorMessage || "Failed to approve deletion request.");
      }
    } catch (e) {
      console.error(e);
      showToast("error", getApolloErrorMessage(e));
    }
  };

  // =============================
  // Monthly grid columns
  // =============================
  const monthlyColumns = useMemo(() => {
    return [
      { field: "id", headerName: "ID", flex: 0.4 },
      {
        field: "amount",
        headerName: "Monthly Amount",
        flex: 0.9,
        valueFormatter: (p) => (p.value != null ? Number(p.value).toLocaleString() : "-"),
      },
      {
        field: "dueDate",
        headerName: "Due Day",
        flex: 0.6,
        valueFormatter: (p) => (p.value != null ? `Day ${p.value}` : "-"),
      },
      {
        field: "isActive",
        headerName: "Status",
        flex: 0.7,
        renderCell: (params) =>
          params.value ? <Chip size="small" label="Active" /> : <Chip size="small" label="Inactive" variant="outlined" />,
      },
      { field: "createdDate", headerName: "Created", flex: 0.9 },
      { field: "updatedDate", headerName: "Updated", flex: 0.9 },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1.4,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          const isActive = !!row?.isActive;

          const anotherActiveExists = !!activeMonthly && String(activeMonthly.id) !== String(row.id);
          const disableActivate = togglingMonthly || isActive || anotherActiveExists;
          const disableDeactivate = togglingMonthly || !isActive;

          return (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                disabled={disableActivate}
                onClick={() => handleToggleMonthlyActive(row, true)}
              >
                Approve
              </Button>

              <Button
                size="small"
                variant="outlined"
                disabled={disableDeactivate}
                onClick={() => handleToggleMonthlyActive(row, false)}
              >
                Deactivate
              </Button>
            </Stack>
          );
        },
      },
    ];
  }, [activeMonthly, togglingMonthly]);

  return (
    <Box m="1.5rem 2.5rem">
      <Header title="CONTRIBUTION POLICY" subtitle="Monthly contribution setup and schedule generation" />

      <Snackbar
        open={!!toast.msg}
        autoHideDuration={4500}
        onClose={() => setToast({ type: "success", msg: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setToast({ type: "success", msg: "" })} severity={toast.type} sx={{ width: "100%" }}>
          {toast.msg}
        </Alert>
      </Snackbar>

      {/* Summary */}
      <Card sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={800}>
            Current Policy
          </Typography>
          <Divider sx={{ my: 1.5, opacity: 0.35 }} />

          {activeMonthly ? (
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
              <Chip
                label={`KES ${Number(activeMonthly.amount).toLocaleString()} per month`}
                sx={{ fontWeight: 800 }}
              />
              <Chip label={`Due: Day ${activeMonthly.dueDate}`} variant="outlined" />
              <Typography variant="body2" sx={{ color: theme.palette.secondary[200] }}>
                New policies can be created, but must be approved/activated (maker-checker) to take effect.
              </Typography>
            </Stack>
          ) : (
            <Alert severity="warning">No active monthly contribution policy found. Create one, then approve/activate it.</Alert>
          )}
        </CardContent>
      </Card>

      {/* 1) Monthly Contribution Setup */}
      <MonthlyContributionForm theme={theme} onSave={handleCreateMonthly} saving={creatingMonthly} />

      {/* Monthly contributions grid (SHORT) */}
      <Card sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={800}>
            Monthly Contribution Policies
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.secondary[200], mt: 0.5 }}>
            Only one policy is active at a time. Others are historical.
          </Typography>

          <Divider sx={{ my: 2, opacity: 0.35 }} />

          {/* ✅ Short grid: ~3 rows visible */}
          <Box mt={1} height={260} sx={gridSx(theme)}>
            <DataGrid
              loading={mcLoading}
              rows={sortedMonthly}
              getRowId={(row) => row.id}
              columns={monthlyColumns}
              density="compact"
              pageSizeOptions={[3, 5, 10]}
              initialState={{
                pagination: { paginationModel: { pageSize: 3, page: 0 } },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* 2) Populate schedule */}
      <Card sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={800}>
            2) Populate Contribution Schedule
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.secondary[200], mt: 0.5 }}>
            Generates schedule entries between the selected months for the year provided.
          </Typography>

          <Divider sx={{ my: 2, opacity: 0.35 }} />

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField label="Year" value={popYear} onChange={(e) => setPopYear(e.target.value)} fullWidth />
            <TextField
              label="Start Month (1-12)"
              value={popStartMonth}
              onChange={(e) => setPopStartMonth(e.target.value)}
              type="number"
              fullWidth
            />
            <TextField
              label="End Month (1-12)"
              value={popEndMonth}
              onChange={(e) => setPopEndMonth(e.target.value)}
              type="number"
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handlePopulateSchedule}
              disabled={populating}
              sx={{ borderRadius: 2, fontWeight: 800, minWidth: 220 }}
            >
              {populating ? "Populating..." : "Populate Schedule"}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Optional: Schedule deletion flow */}
      <Accordion sx={{ borderRadius: 3, overflow: "hidden" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={800}>Optional: Schedule Deletion (Maker-Checker)</Typography>
        </AccordionSummary>

        <AccordionDetails>
          <Card sx={{ borderRadius: 3, mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={800}>
                A) Request Schedule Deletion
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.secondary[200], mt: 0.5 }}>
                Submits a deletion request for approval.
              </Typography>

              <Divider sx={{ my: 2, opacity: 0.35 }} />

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField label="Schedule Year" value={delYear} onChange={(e) => setDelYear(e.target.value)} fullWidth />
                <TextField
                  label="Start Month (1-12)"
                  value={delStartMonth}
                  onChange={(e) => setDelStartMonth(e.target.value)}
                  type="number"
                  fullWidth
                />
                <TextField
                  label="End Month (1-12)"
                  value={delEndMonth}
                  onChange={(e) => setDelEndMonth(e.target.value)}
                  type="number"
                  fullWidth
                />
                <Button
                  variant="outlined"
                  onClick={handleRequestDeletion}
                  disabled={requestingDeletion}
                  sx={{ borderRadius: 2, fontWeight: 800, minWidth: 230 }}
                >
                  {requestingDeletion ? "Submitting..." : "Request Deletion"}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={800}>
                B) Approve Deletion Request
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.secondary[200], mt: 0.5 }}>
                Approver enters the request ID and approves.
              </Typography>

              <Divider sx={{ my: 2, opacity: 0.35 }} />

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Deletion Request ID"
                  value={approveRequestId}
                  onChange={(e) => setApproveRequestId(e.target.value)}
                  type="number"
                  fullWidth
                />
                <Button
                  variant="contained"
                  onClick={handleApproveDeletion}
                  disabled={approvingDeletion}
                  sx={{ borderRadius: 2, fontWeight: 800, minWidth: 230 }}
                >
                  {approvingDeletion ? "Approving..." : "Approve Deletion"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ContributionPolicy;